import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { searchGoogleFonts, getSystemFonts, getFontFamilyCSS } from '../utils/googleFonts';

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * FontPicker Component
 * A searchable dropdown for selecting Google Fonts and system fonts
 */
const FontPicker = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load initial fonts when dropdown opens
  useEffect(() => {
    if (isOpen && fonts.length === 0) {
      loadFonts('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Load fonts with search
  const loadFonts = async (search) => {
    setLoading(true);
    try {
      const allSystemFonts = getSystemFonts();
      const googleFonts = await searchGoogleFonts(search);

      // Filter system fonts if there's a search term
      const filteredSystemFonts = search
        ? allSystemFonts.filter(f => f.toLowerCase().includes(search.toLowerCase()))
        : allSystemFonts;

      // Combine filtered system fonts and Google fonts
      const combinedFonts = [];

      if (filteredSystemFonts.length > 0) {
        combinedFonts.push(...filteredSystemFonts);
      }

      if (googleFonts.length > 0) {
        if (filteredSystemFonts.length > 0) {
          combinedFonts.push({ divider: true, label: 'Google Fonts' });
        }
        combinedFonts.push(...googleFonts);
      }

      setFonts(combinedFonts);
    } catch (error) {
      console.error('Error loading fonts:', error);
      setFonts(getSystemFonts());
    } finally {
      setLoading(false);
    }
  };

  // Debounced font loading
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadFonts = useCallback(
    debounce((search) => {
      loadFonts(search);
    }, 150),
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedLoadFonts(value);
  };

  // Handle font selection
  const handleSelectFont = (font) => {
    if (font.divider) return;
    onChange(font);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Get display value
  const displayValue = value || 'System Default';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected font display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between"
        style={{ fontFamily: getFontFamilyCSS(value) }}
      >
        <span className="truncate">{displayValue}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search fonts..."
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    loadFonts('');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Font list */}
          <div className="overflow-y-auto max-h-64">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Loading fonts...
              </div>
            ) : fonts.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No fonts found
              </div>
            ) : (
              fonts.map((font, index) => {
                if (font.divider) {
                  return (
                    <div
                      key={`divider-${index}`}
                      className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-t border-b border-gray-200"
                    >
                      {font.label}
                    </div>
                  );
                }

                const fontFamily = typeof font === 'string' ? font : font.family;
                const isSelected = fontFamily === displayValue;

                return (
                  <button
                    key={fontFamily}
                    type="button"
                    onClick={() => handleSelectFont(fontFamily)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition-colors ${
                      isSelected ? 'bg-indigo-100 text-indigo-900' : 'text-gray-700'
                    }`}
                    style={{ fontFamily: getFontFamilyCSS(fontFamily) }}
                  >
                    {fontFamily}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FontPicker;
